import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addUtmToContentLinks } from '~/modules/tracking/util/add-utm-to-content-links';

import { cn } from '@rocco/utils/cn';
import { RICH_TEXT_HTML_CLASS } from '~/ui-rocco/pages/detail-shared';
import {
    htmlToPlainText,
    sentenceEndOffsets,
} from '~/ui-rocco/pages/detail-shared/sentence-offsets';

/**
 * The statement's type, matching the product directory's own SEO statement so
 * the two read as the same voice at the same size.
 *
 * This is the one to change if the size wants tweaking. Nothing else reads it.
 */
const STATEMENT_TYPE = cn(
    'font-sans text-[1.1256rem] leading-[1.2] font-normal tracking-[-0.03em]',
    'md:text-[2.0625rem] md:leading-[1.25] md:font-medium md:tracking-[-0.028em]',
);

/**
 * What the copy drops to once the statement has had its say.
 *
 * A description that runs to a dozen lines is not a statement any more, it is
 * the supplier's notes — lead times, what is included. Holding all of it at
 * statement size gives a paragraph about GST the same weight as the line about
 * what the thing is.
 */
const TAIL_TYPE = cn(
    'font-sans text-[0.9375rem] leading-[1.45] font-normal tracking-normal',
    'md:text-[1.125rem] md:leading-[1.5]',
);

/** How much shows before Read more. */
const STATEMENT_LINES = 3;

/** How many lines stay at statement size once expanded. */
const STATEMENT_MAX_LINES = 8;

/** One ink throughout: the copy does not change character by growing. */
const INK = 'text-[#323232]';

/**
 * The supplier's own lead, in the statement's place when they have written
 * one. Shown whole: it is capped at the statement's three lines, so there is
 * nothing to clamp and no Read more, and the description it stands in for
 * follows in full beside the picture. Rich text with the description's
 * formatting, its links tracked the way the description's are.
 */
export const ProductTopStatement = ({
    html,
    className,
    onLinkClick,
}: {
    html: string;
    className?: string;
    onLinkClick?: React.MouseEventHandler;
}) => {
    const trackedHtml = useMemo(() => addUtmToContentLinks(html), [html]);
    return (
        <div
            className={cn(STATEMENT_TYPE, INK, RICH_TEXT_HTML_CLASS, className)}
            onClick={onLinkClick}
            onAuxClick={onLinkClick}
            onContextMenu={onLinkClick}
            dangerouslySetInnerHTML={{ __html: trackedHtml }}
        />
    );
};

interface ProductStatementTextProps {
    /** The supplier's description, as the CMS stores it. */
    html: string;
    /**
     * Read more's own action, in place of opening the copy here — the page
     * sends the reader to the description in full beside the picture, as the
     * project page does. Answers false when there is nowhere to send them,
     * and the copy opens in place instead.
     */
    onReadMore?: () => boolean;
    className?: string;
    /**
     * Rewrites a link the supplier wrote into the copy on its way out, so it
     * carries the same tracking the page's own buttons do.
     */
    onLinkClick?: React.MouseEventHandler;
}

/**
 * The product's description, leading the page: three lines, then Read more.
 *
 * The collapsed cap is a hard height rather than a sentence crop. A sentence
 * crop cannot promise three lines — it can only cut where the author put a full
 * stop, and a product whose opening sentence runs six lines gets six. `lh` is
 * the element's own line height, so the cap holds at both type scales without
 * restating them.
 *
 * Expanded, the copy steps down to smaller type once it passes
 * {@link STATEMENT_MAX_LINES}, and the step lands on a sentence end so no
 * sentence changes size halfway through. Everything expanded is text rather
 * than markup, because the split is made at a character offset and slicing
 * markup there would not close its tags.
 *
 * There is no Read less. Expanded, the copy itself is the control: clicking it
 * collapses it again. It carries a button role and answers the keyboard, so
 * dropping the visible label does not drop the affordance for anyone driving
 * the page without a pointer.
 */
export const ProductStatementText = ({
    html,
    onReadMore,
    className,
    onLinkClick,
}: ProductStatementTextProps) => {
    const bodyRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    // Where the collapsed copy is cut, in px from its top: the foot of its
    // third line, wherever that line sits. `null` until measured, or when
    // the whole of it fits.
    const [cap, setCap] = useState<number | null>(null);
    // Where the copy steps down. `null` until measured, and equal to the
    // length when the whole of it fits inside the statement's lines.
    const [splitAt, setSplitAt] = useState<number | null>(null);

    const plainText = useMemo(() => htmlToPlainText(html), [html]);

    /**
     * Whether there is anything behind the collapsed cap worth offering, and
     * where that cap falls.
     *
     * Measured on the line boxes rather than set as three line-heights: the
     * description is CMS markup, and a paragraph break between its lines
     * adds a margin no multiple of the line height allows for, so a fixed
     * cap fell across the third line and cut it in half. A `Range` over the
     * copy gives one client rect per line box; the third line's foot is the
     * cut, so the line above it is whole and the one below is not seen.
     */
    const measureOverflow = useCallback(() => {
        const el = bodyRef.current;
        if (!el) return;
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '0');
        if (lineHeight <= 0) return;
        const top = el.getBoundingClientRect().top;
        const range = document.createRange();
        range.selectNodeContents(el);
        // Block boxes come back as rects too; a line box is never taller
        // than the line itself, which is what tells them apart.
        const rects = [...range.getClientRects()]
            .filter(
                (rect) => rect.height > 0 && rect.height <= lineHeight * 1.5,
            )
            .sort((a, b) => a.top - b.top);
        const lines: { top: number; bottom: number }[] = [];
        for (const rect of rects) {
            const last = lines[lines.length - 1];
            if (last && rect.top < last.bottom - 1) {
                last.bottom = Math.max(last.bottom, rect.bottom);
            } else {
                lines.push({ top: rect.top, bottom: rect.bottom });
            }
        }
        const third = lines[STATEMENT_LINES - 1];
        if (lines.length > STATEMENT_LINES && third) {
            setCap(Math.ceil(third.bottom - top));
            setOverflows(true);
        } else {
            setCap(null);
            setOverflows(false);
        }
    }, []);

    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        measureOverflow();
        document.fonts?.ready.then(measureOverflow).catch(() => {});
        const observer = new ResizeObserver(measureOverflow);
        observer.observe(el);
        return () => observer.disconnect();
    }, [measureOverflow, html]);

    /**
     * The first sentence end that reaches the statement's line allowance,
     * measured on a copy of the text at the statement's own size and width.
     *
     * A `Range` over the text node is what gives line counts: its client rects
     * are one per line box, so counting them to an offset says how many lines
     * that much copy takes.
     */
    const measureSplit = useCallback(() => {
        const el = measureRef.current;
        const node = el?.firstChild;
        if (!el || !node) return;
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '0');
        if (lineHeight <= 0) return;

        const range = document.createRange();
        const linesTo = (offset: number) => {
            range.setStart(node, 0);
            range.setEnd(node, Math.min(offset, plainText.length));
            return range.getClientRects().length;
        };

        // The lead never runs past the supplier's first paragraph. Sized on
        // line count alone it took whatever fitted in eight, so a description
        // that opened with a short paragraph carried the second one at heading
        // size as well and the page led with two claims instead of one.
        const paragraphBreak = plainText.indexOf('\n');
        const leadEnd =
            paragraphBreak === -1 ? plainText.length : paragraphBreak;

        const offsets = sentenceEndOffsets(plainText);
        const fitted =
            linesTo(plainText.length) <= STATEMENT_MAX_LINES
                ? plainText.length
                : (offsets.find(
                      (offset) => linesTo(offset) >= STATEMENT_MAX_LINES,
                  ) ?? plainText.length);
        // A single paragraph long enough to overrun eight lines is still cut
        // at a sentence, so the lead never ends mid-thought.
        setSplitAt(Math.min(fitted, leadEnd));
    }, [plainText]);

    useEffect(() => {
        if (!expanded) return;
        const el = measureRef.current;
        if (!el) return;
        measureSplit();
        document.fonts?.ready.then(measureSplit).catch(() => {});
        const observer = new ResizeObserver(measureSplit);
        observer.observe(el);
        return () => observer.disconnect();
    }, [expanded, measureSplit]);

    const collapse = () => setExpanded(false);
    const head = splitAt === null ? plainText : plainText.slice(0, splitAt);
    const tail = splitAt === null ? '' : plainText.slice(splitAt).trimStart();

    const collapseHandlers = {
        role: 'button',
        tabIndex: 0,
        'aria-expanded': true,
        onClick: collapse,
        onKeyDown: (event: React.KeyboardEvent) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            collapse();
        },
    } as const;

    if (expanded) {
        return (
            // Positioned, so the off-screen twin's `w-full` resolves to this
            // column rather than to whatever ancestor happens to be
            // positioned. Measuring at the wrong width counts the wrong number
            // of lines, and the copy steps down in the wrong place.
            <div className={cn('relative', className)}>
                {/* Off-screen twin at the statement's size and width, which is
                    what the line counting measures. The visible copy cannot be
                    measured for this: it is already split. */}
                <div
                    ref={measureRef}
                    aria-hidden="true"
                    className={cn(
                        STATEMENT_TYPE,
                        'pointer-events-none absolute -z-10 w-full whitespace-pre-line opacity-0',
                    )}
                >
                    {plainText}
                </div>

                <div {...collapseHandlers} className="cursor-pointer">
                    <div
                        className={cn(
                            STATEMENT_TYPE,
                            INK,
                            'whitespace-pre-line',
                        )}
                    >
                        {head}
                    </div>
                    {tail ? (
                        <div
                            className={cn(
                                TAIL_TYPE,
                                INK,
                                'mt-[0.75rem] whitespace-pre-line',
                            )}
                        >
                            {tail}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className={className}>
            {/* The CMS markup renders as-is here, so it needs the shared
                rich-text class: Tailwind's preflight strips list markers,
                paragraph margins and link underlines, and without it a
                description's bullets, paragraph gaps and links all read as
                one undifferentiated run of copy. */}
            <div
                ref={bodyRef}
                className={cn(
                    STATEMENT_TYPE,
                    INK,
                    RICH_TEXT_HTML_CLASS,
                    'overflow-hidden',
                    // Three lines until the line boxes have been measured,
                    // so the first paint is already the right height.
                    cap === null && 'max-h-[3lh]',
                )}
                style={cap === null ? undefined : { maxHeight: cap }}
                onClick={onLinkClick}
                // Middle-click opens the link too, and it must arrive
                // carrying the same tracking a left click does.
                onAuxClick={onLinkClick}
                onContextMenu={onLinkClick}
                dangerouslySetInnerHTML={{ __html: addUtmToContentLinks(html) }}
            />

            {overflows ? (
                <button
                    type="button"
                    onClick={() => {
                        if (onReadMore?.()) return;
                        setExpanded(true);
                    }}
                    aria-expanded={false}
                    className={cn(
                        'mt-[0.5rem] cursor-pointer border-0 bg-transparent p-0',
                        'font-sans text-[1rem] leading-[1.2] font-medium tracking-[-0.02em]',
                        'text-[#767676] transition-colors hover:text-[#323232]',
                    )}
                >
                    Read more
                </button>
            ) : null}
        </div>
    );
};
